import React, { useState, useContext, useRef } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { formatPhone } from '@/components/admin/util';
import { policyStatus, POLICY_STATUS_META, ADMIN_CLASSIFICATION, ADMIN_SERVICES } from '@/lib/providerDivisions';
import { isGlobalAdmin } from '@/lib/workspaces';
import ProviderDocuments from './ProviderDocuments';

const PROVIDER_TYPES = ['Subcontractor', 'Third-Party Service Provider', 'Supplier', 'Other'];
const TRADES = ['Janitorial', 'Event Support', 'Landscape', 'Security', 'Sweeping', 'General Maintenance', 'Special Projects', 'HVAC', 'Plumbing', 'Electrical'];
const DIVISIONS = ['Janitorial', 'Landscaping', 'Security', 'Sweeping', 'General Maintenance'];
const PRIORITIES = ['P1', 'P2', 'P3', 'P4', 'P5'];
const RATE_BASES = ['Flat service call', 'Hourly', 'Quote required'];

const Section = ({ title, children }) => (
  <div className="space-y-3">
    <h4 className="text-xs font-bold uppercase tracking-wider text-[#8FC93A]">{title}</h4>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>
  </div>
);
const F = ({ label, children, full }) => (
  <div className={`space-y-1 ${full ? 'md:col-span-2' : ''}`}>
    <Label className="text-xs text-slate-600">{label}</Label>
    {children}
  </div>
);

const EMPTY = {
  company_name: '', provider_type: 'Subcontractor', trade_types: [], divisions: [], specialties: '',
  specialty_trades_text: '',
  company_experience: '',
  contact_name: '', contact_first_name: '', contact_last_name: '', contact_email: '', contact_phone: '',
  secondary_contact_name: '', secondary_contact_email: '', secondary_contact_phone: '',
  address: '', city: '', state: '', zip: '',
  service_states: '', service_areas: '',
  service_counties: [], partial_counties: [],
  insurance_carrier: '', insurance_policy_number: '', insurance_expiry: '',
  coi_on_file: false, w9_on_file: false, hourly_rate: '', rate_notes: '',
  accepts_p1: '', priority_commitments: [], service_rates: [],
  classifications: [], admin_services: [], service_mode: '',
  insured_name: '', producer_name: '', producer_contact: '', certificate_holder: '', certificate_issue_date: '',
  insurance_policies: [],
  notes: '', internal_notes: '',
};

const ProviderForm = ({ provider, onClose, onSaved }) => {
  const { token, user } = useContext(AuthContext);
  const isEdit = !!provider;
  const [prov, setProv] = useState(provider);
  const canManageDocs = ['owner', 'admin', 'manager'].includes(user?.role) || isGlobalAdmin(user);
  const [f, setF] = useState(() => {
    if (!provider) return { ...EMPTY };
    return {
      ...EMPTY, ...provider,
      service_states: (provider.service_states || []).join(', '),
      service_counties_text: (provider.service_counties || []).join(', '),
      partial_counties_text: (provider.partial_counties || []).join(', '),
      specialty_trades_text: (provider.specialty_trades || []).join(', '),
      accepts_p1: provider.accepts_p1 === true ? 'yes' : provider.accepts_p1 === false ? 'no' : '',
      priority_commitments: provider.priority_commitments || [],
      service_rates: provider.service_rates || [],
      classifications: provider.classifications || [],
      admin_services: provider.admin_services || [],
      insurance_policies: provider.insurance_policies || [],
    };
  });
  const [busy, setBusy] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [extractResult, setExtractResult] = useState(null);
  const baseline = useRef(null);
  if (baseline.current === null) baseline.current = JSON.stringify(f);
  const confirmClose = () => {
    if (JSON.stringify(f) !== baseline.current
      && !window.confirm('You have unsaved changes.\n\nOK = Discard changes and close.\nCancel = Continue editing (use Save to keep them).')) return;
    onClose();
  };
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const toggle = (k) => () => setF((p) => ({ ...p, [k]: !p[k] }));
  const blurPhone = (k) => () => setF((p) => ({ ...p, [k]: formatPhone(p[k]) }));
  const toggleTrade = (t) => setF((p) => ({
    ...p, trade_types: p.trade_types.includes(t) ? p.trade_types.filter((x) => x !== t) : [...p.trade_types, t],
  }));
  const toggleDivision = (d) => setF((p) => ({
    ...p, divisions: p.divisions.includes(d) ? p.divisions.filter((x) => x !== d) : [...p.divisions, d],
  }));
  const addRow = (key, row) => setF((p) => ({ ...p, [key]: [...(p[key] || []), row] }));
  const setRow = (key, i, patch) => setF((p) => ({ ...p, [key]: p[key].map((r, idx) => idx === i ? { ...r, ...patch } : r) }));
  const delRow = (key, i) => setF((p) => ({ ...p, [key]: p[key].filter((_, idx) => idx !== i) }));
  const toggleIn = (key, val) => setF((p) => ({
    ...p, [key]: (p[key] || []).includes(val) ? p[key].filter((x) => x !== val) : [...(p[key] || []), val],
  }));

  const coiDoc = (prov?.documents || []).filter((d) => (d.type || '').toLowerCase() === 'coi').slice(-1)[0];
  const runExtract = async (retry = false) => {
    if (!coiDoc) { toast.error('Upload a COI document first (in the provider card), then extract.'); return; }
    setExtracting(true); setExtractResult(null);
    try {
      const r = await axios.post(`${API}/service-providers/${provider.id}/documents/${coiDoc.id}/extract-insurance?retry=${retry}`,
        {}, { headers: { Authorization: `Bearer ${token}` } });
      setExtractResult(r.data);
      if (r.data.error) toast.error(`Extraction issue: ${r.data.error}`);
      else toast.success(`Extracted ${(r.data.candidate?.policies || []).length} policy candidate(s) — review below`);
    } catch { toast.error('Extraction failed — the document is still saved; enter details manually.'); }
    finally { setExtracting(false); }
  };
  const applyCandidate = () => {
    const c = extractResult?.candidate; if (!c) return;
    const existingKeys = new Set((f.insurance_policies || []).map((p) => `${(p.coverage_type || '').toLowerCase()}|${(p.policy_number || '').toLowerCase()}`));
    const add = (c.policies || [])
      .filter((p) => !existingKeys.has(`${(p.coverage_type || '').toLowerCase()}|${(p.policy_number || '').toLowerCase()}`))
      .map((p) => ({
        id: `pol-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        coverage_type: p.coverage_type || '', carrier: p.carrier || '', policy_number: p.policy_number || '',
        effective_date: p.effective_date || '', expiration_date: p.expiration_date || '',
        limits: p.limits || '', source_document_id: coiDoc?.id || null, superseded: false,
      }));
    setF((prev) => ({
      ...prev,
      insured_name: prev.insured_name || c.insured_name || '',
      producer_name: prev.producer_name || c.producer_name || '',
      producer_contact: prev.producer_contact || c.producer_contact || '',
      certificate_holder: prev.certificate_holder || c.certificate_holder || '',
      certificate_issue_date: prev.certificate_issue_date || c.certificate_issue_date || '',
      insurance_policies: [...(prev.insurance_policies || []), ...add],
    }));
    toast.success(`Applied ${add.length} new policy row(s). Review, then Save to persist.`);
    setExtractResult(null);
  };

  const submit = async () => {
    if (!f.company_name.trim()) { toast.error('Company name is required'); return; }
    // An already-Qualified provider must keep a valid email (partial saves otherwise allowed).
    const emailRe = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
    if (isEdit && provider.qualification_status === 'qualified'
      && !(f.contact_email && emailRe.test(f.contact_email.trim()))) {
      toast.error('A Qualified Service Provider must keep a valid email address. Your changes are kept — fix the email and save again.');
      return;
    }
    setBusy(true);
    const splitCsv = (v) => (v ? v.split(',').map((s) => s.trim()).filter(Boolean) : []);
    const payload = {
      ...f,
      hourly_rate: f.hourly_rate === '' ? null : Number(f.hourly_rate),
      service_states: f.service_states ? f.service_states.split(',').map((s) => s.trim().toUpperCase()).filter(Boolean) : [],
      service_counties: splitCsv(f.service_counties_text),
      partial_counties: splitCsv(f.partial_counties_text),
      specialty_trades: splitCsv(f.specialty_trades_text),
      accepts_p1: f.accepts_p1 === 'yes' ? true : f.accepts_p1 === 'no' ? false : null,
    };
    delete payload.service_counties_text;
    delete payload.partial_counties_text;
    delete payload.specialty_trades_text;
    try {
      if (isEdit) {
        await axios.put(`${API}/service-providers/${provider.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Service provider updated');
      } else {
        await axios.post(`${API}/service-providers`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Service provider created');
      }
      onSaved?.();
      onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Could not save provider'));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) confirmClose(); }}>
      <DialogContent className="max-w-3xl max-h-[88vh] flex flex-col overflow-hidden p-0 gap-0" data-testid="provider-form-dialog"
        onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => { e.preventDefault(); confirmClose(); }}>
        <div className="shrink-0 border-b bg-white px-6 pt-6 pb-3">
          <DialogHeader>
            <DialogTitle className="text-[#0E2A47] pr-8">{isEdit ? `Edit Provider — ${provider.company_name}` : 'Add Service Provider'}</DialogTitle>
          </DialogHeader>
          <div className="mt-3 flex items-center justify-end gap-2">
            <button onClick={confirmClose} data-testid="prov-cancel-top" className="rounded-md border border-gray-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-gray-50">Cancel</button>
            <button onClick={submit} disabled={busy} data-testid="prov-save-top" className="rounded-md bg-[#0E2A47] px-5 py-2 text-sm font-semibold text-white hover:bg-[#16324F] disabled:opacity-50">{busy ? 'Saving…' : (isEdit ? 'Save Changes' : 'Create Provider')}</button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-6">
          <Section title="Company">
            <F label="Company Name *"><Input value={f.company_name} onChange={set('company_name')} data-testid="prov-name" /></F>
            <F label="Provider Type">
              <select value={f.provider_type} onChange={set('provider_type')} data-testid="prov-type"
                className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm">
                {PROVIDER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </F>
            <F label="Service Divisions (website services)" full>
              <div className="flex flex-wrap gap-2" data-testid="prov-divisions">
                {DIVISIONS.map((d) => {
                  const on = f.divisions.includes(d);
                  return (
                    <button type="button" key={d} onClick={() => toggleDivision(d)}
                      className={`rounded-full px-3 py-1 text-xs font-medium border transition-colors ${on ? 'bg-[#4F9417] text-white border-[#4F9417]' : 'bg-white text-slate-600 border-gray-200 hover:bg-gray-50'}`}>
                      {d}
                    </button>
                  );
                })}
              </div>
            </F>
            <F label="Specific trades / specialties" full>
              <div className="flex flex-wrap gap-2" data-testid="prov-trades">
                {TRADES.map((t) => {
                  const on = f.trade_types.includes(t);
                  return (
                    <button type="button" key={t} onClick={() => toggleTrade(t)}
                      className={`rounded-full px-3 py-1 text-xs font-medium border transition-colors ${on ? 'bg-[#0E2A47] text-white border-[#0E2A47]' : 'bg-white text-slate-600 border-gray-200 hover:bg-gray-50'}`}>
                      {t}
                    </button>
                  );
                })}
              </div>
            </F>
            <F label="Specialty Trades (specific work performed, comma-separated)" full>
              <Input value={f.specialty_trades_text} onChange={set('specialty_trades_text')} data-testid="prov-specialty-trades"
                placeholder="e.g. Window and door replacement, Pressure washing" />
              <p className="mt-1 text-[11px] text-slate-400">Leave blank if unknown — the record shows “Not specified” and still saves. Division slices stay independent of these.</p>
            </F>
            <F label="Notes on Specialties" full><Input value={f.specialties} onChange={set('specialties')} /></F>
            <F label="Brief company experience (optional)" full><Textarea rows={2} value={f.company_experience} onChange={set('company_experience')} data-testid="prov-experience" /></F>
          </Section>

          <Section title="Primary Contact">
            <F label="Contact First Name"><Input value={f.contact_first_name} onChange={set('contact_first_name')} data-testid="prov-first-name" /></F>
            <F label="Contact Last Name"><Input value={f.contact_last_name} onChange={set('contact_last_name')} data-testid="prov-last-name" /></F>
            <F label="Display Name (if different)"><Input value={f.contact_name} onChange={set('contact_name')} /></F>
            <div />
            <F label={isEdit && provider.qualification_status === 'qualified' ? 'Email * (required for a Qualified provider)' : 'Email (required for qualification)'}>
              <Input value={f.contact_email} onChange={set('contact_email')} data-testid="prov-email" />
            </F>
            <F label="Phone *"><Input value={f.contact_phone} onChange={set('contact_phone')} onBlur={blurPhone('contact_phone')} data-testid="prov-phone" /></F>
            <F label="Secondary Contact Name"><Input value={f.secondary_contact_name} onChange={set('secondary_contact_name')} /></F>
            <F label="Secondary Contact Email"><Input value={f.secondary_contact_email} onChange={set('secondary_contact_email')} /></F>
            <F label="Secondary Contact Phone"><Input value={f.secondary_contact_phone} onChange={set('secondary_contact_phone')} onBlur={blurPhone('secondary_contact_phone')} /></F>
          </Section>

          <Section title="Address & Coverage">
            <F label="Address" full><Input value={f.address} onChange={set('address')} /></F>
            <F label="City *"><Input value={f.city} onChange={set('city')} /></F>
            <F label="State"><Input value={f.state} onChange={set('state')} /></F>
            <F label="ZIP *"><Input value={f.zip} onChange={set('zip')} /></F>
            <F label="Service States (comma-separated)"><Input value={f.service_states} onChange={set('service_states')} placeholder="FL, GA, AL" /></F>
            <F label="Service Areas covered (cities/counties, comma-separated)" full><Input value={f.service_areas} onChange={set('service_areas')} data-testid="prov-service-areas" /></F>
            <F label="Full-county coverage (comma-separated — inherits all cities)"><Input value={f.service_counties_text} onChange={set('service_counties_text')} data-testid="prov-counties" placeholder="Miami-Dade, Broward" /></F>
            <F label="Partial counties (restricted coverage)"><Input value={f.partial_counties_text} onChange={set('partial_counties_text')} data-testid="prov-partial-counties" /></F>
          </Section>

          <Section title="Response Capability">
            <F label="Accepts P1 emergencies?">
              <select value={f.accepts_p1} onChange={set('accepts_p1')} data-testid="prov-accepts-p1" className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm">
                <option value="">Unknown — not specified</option>
                <option value="yes">Yes — accepts P1</option>
                <option value="no">No</option>
              </select>
            </F>
            <div className="md:col-span-2">
              <div className="mb-1 flex items-center justify-between">
                <Label className="text-xs text-slate-600">Response commitments (P1–P5 by service / area)</Label>
                <button type="button" onClick={() => addRow('priority_commitments', { priority: 'P1', service: '', area: '', response_hours: '' })}
                  data-testid="add-commitment" className="text-xs font-semibold text-[#0E2A47] hover:underline">+ Add</button>
              </div>
              {(f.priority_commitments || []).map((c, i) => (
                <div key={i} className="mb-1 flex flex-wrap items-center gap-1.5" data-testid={`commitment-row-${i}`}>
                  <select value={c.priority} onChange={(e) => setRow('priority_commitments', i, { priority: e.target.value })} className="rounded border border-gray-200 px-2 py-1 text-xs">
                    {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <Input className="h-8 w-32 text-xs" placeholder="Service" value={c.service || ''} onChange={(e) => setRow('priority_commitments', i, { service: e.target.value })} />
                  <Input className="h-8 w-32 text-xs" placeholder="Area" value={c.area || ''} onChange={(e) => setRow('priority_commitments', i, { area: e.target.value })} />
                  <Input className="h-8 w-24 text-xs" placeholder="Hours" value={c.response_hours || ''} onChange={(e) => setRow('priority_commitments', i, { response_hours: e.target.value })} />
                  <button type="button" onClick={() => delRow('priority_commitments', i)} className="text-rose-500">✕</button>
                </div>
              ))}
              <p className="text-[11px] text-slate-400">P1 1–2h · P2 same day · P3 same week · P4 ≤30 days · P5 special projects. Leave unknown rather than overstating.</p>
            </div>
          </Section>

          <Section title="Services & Agreed Rates (provider payouts)">
            <div className="md:col-span-2">
              <div className="mb-1 flex items-center justify-between">
                <Label className="text-xs text-slate-600">Agreed rates — amounts payable to the provider (not customer billing)</Label>
                <button type="button" onClick={() => addRow('service_rates', { division: '', service: '', priority: '', rate: '', rate_basis: 'Flat service call', travel_type: '', travel_rate: '', travel_included: false, area: '', notes: '' })}
                  data-testid="add-rate" className="text-xs font-semibold text-[#0E2A47] hover:underline">+ Add rate</button>
              </div>
              {(f.service_rates || []).map((r, i) => (
                <div key={i} className="mb-2 rounded-md border border-slate-100 p-2" data-testid={`rate-row-${i}`}>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <select value={r.division || ''} onChange={(e) => setRow('service_rates', i, { division: e.target.value })} className="rounded border border-gray-200 px-2 py-1 text-xs">
                      <option value="">Division…</option>{DIVISIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                    <Input className="h-8 w-36 text-xs" placeholder="Service / trade" value={r.service || ''} onChange={(e) => setRow('service_rates', i, { service: e.target.value })} />
                    <select value={r.priority || ''} onChange={(e) => setRow('service_rates', i, { priority: e.target.value })} className="rounded border border-gray-200 px-2 py-1 text-xs">
                      <option value="">Std</option>{PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                    <Input className="h-8 w-24 text-xs" placeholder="Payout $" value={r.rate || ''} onChange={(e) => setRow('service_rates', i, { rate: e.target.value })} />
                    <select value={r.rate_basis || ''} onChange={(e) => setRow('service_rates', i, { rate_basis: e.target.value })} className="rounded border border-gray-200 px-2 py-1 text-xs">
                      {RATE_BASES.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                    <button type="button" onClick={() => delRow('service_rates', i)} className="ml-auto text-rose-500">✕</button>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    <Input className="h-8 w-32 text-xs" placeholder="Coverage area" value={r.area || ''} onChange={(e) => setRow('service_rates', i, { area: e.target.value })} />
                    <select value={r.travel_type || ''} onChange={(e) => setRow('service_rates', i, { travel_type: e.target.value })} className="rounded border border-gray-200 px-2 py-1 text-xs">
                      <option value="">No travel charge</option><option value="Flat per visit">Flat per visit</option><option value="Per mile">Per mile</option>
                    </select>
                    <Input className="h-8 w-24 text-xs" placeholder="Travel $" value={r.travel_rate || ''} onChange={(e) => setRow('service_rates', i, { travel_rate: e.target.value })} />
                    <label className="flex items-center gap-1 text-xs text-slate-600"><input type="checkbox" checked={!!r.travel_included} onChange={(e) => setRow('service_rates', i, { travel_included: e.target.checked })} />Travel included</label>
                  </div>
                </div>
              ))}
              <p className="text-[11px] text-slate-400">Unknown rates stay blank (shown as “Not agreed” / “Quote required”), never zero. Missing rates do not block qualification.</p>
            </div>
          </Section>

          <Section title="Classification">
            <F label="Provider classification" full>
              <div className="flex flex-wrap gap-2" data-testid="prov-classifications">
                {['Field Services', ADMIN_CLASSIFICATION].map((c) => {
                  const on = (f.classifications || []).includes(c);
                  return (
                    <button type="button" key={c} onClick={() => toggleIn('classifications', c)}
                      className={`rounded-full px-3 py-1 text-xs font-medium border transition-colors ${on ? 'bg-[#2F6DB5] text-white border-[#2F6DB5]' : 'bg-white text-slate-600 border-gray-200 hover:bg-gray-50'}`}>
                      {c}
                    </button>
                  );
                })}
              </div>
              <p className="mt-1 text-[11px] text-slate-400">A provider may offer field services, administrative services, or both. Administrative-only records need no P1 or Florida county assignments.</p>
            </F>
            {(f.classifications || []).includes(ADMIN_CLASSIFICATION) && (
              <>
                <F label="Administrative services" full>
                  <div className="flex flex-wrap gap-2" data-testid="prov-admin-services">
                    {ADMIN_SERVICES.map((s) => {
                      const on = (f.admin_services || []).includes(s);
                      return (
                        <button type="button" key={s} onClick={() => toggleIn('admin_services', s)}
                          className={`rounded-full px-3 py-1 text-xs font-medium border ${on ? 'bg-[#0E2A47] text-white border-[#0E2A47]' : 'bg-white text-slate-600 border-gray-200 hover:bg-gray-50'}`}>
                          {s}
                        </button>
                      );
                    })}
                  </div>
                </F>
                <F label="Service mode">
                  <select value={f.service_mode || ''} onChange={set('service_mode')} data-testid="prov-service-mode"
                    className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm">
                    <option value="">Not specified</option>
                    <option value="remote">Remote</option>
                    <option value="on-site">On-site</option>
                    <option value="both">Both</option>
                  </select>
                </F>
              </>
            )}
          </Section>

          <Section title="Documents">
            <div className="md:col-span-2">
              {isEdit ? (
                <>
                  <p className="mb-2 text-[11px] text-slate-500" data-testid="docs-edit-note">Upload compliance documents without leaving Edit. Files save immediately — <strong>Cancel discards unsaved field edits, but does not remove a document already uploaded here.</strong> Missing documents never block a partial Save. Upload sets <em>On File</em> only; verification and qualification stay a separate step.</p>
                  <ProviderDocuments provider={prov} token={token} canManage={canManageDocs} idPrefix="edit-"
                    onChanged={(updated) => { setProv(updated); onSaved?.(); }} />
                </>
              ) : (
                <p className="text-sm italic text-slate-400" data-testid="docs-create-note">Create this provider first — then re-open it in Edit to attach W-9, COI, driver's license and other documents.</p>
              )}
            </div>
          </Section>

          <Section title="Insurance (producer kept separate from carriers)">
            <F label="Insured company name"><Input value={f.insured_name} onChange={set('insured_name')} data-testid="prov-insured-name" /></F>
            <F label="Certificate holder"><Input value={f.certificate_holder} onChange={set('certificate_holder')} /></F>
            <F label="Producer / broker name"><Input value={f.producer_name} onChange={set('producer_name')} data-testid="prov-producer-name" /></F>
            <F label="Producer / broker contact"><Input value={f.producer_contact} onChange={set('producer_contact')} /></F>
            <F label="Certificate issue date (not a policy date)"><Input type="date" value={(f.certificate_issue_date || '').slice(0, 10)} onChange={set('certificate_issue_date')} /></F>
            <div />
            <div className="md:col-span-2">
              <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                <Label className="text-xs text-slate-600">Policies / coverages</Label>
                <div className="flex items-center gap-2">
                  {coiDoc && (
                    <button type="button" onClick={() => runExtract(!!extractResult)} disabled={extracting}
                      data-testid="extract-insurance-btn" className="rounded-md border border-violet-400 px-3 py-1 text-xs font-semibold text-violet-700 hover:bg-violet-50 disabled:opacity-50">
                      {extracting ? 'Extracting…' : (extractResult ? 'Re-extract' : 'Extract Insurance Details')}
                    </button>
                  )}
                  <button type="button" onClick={() => addRow('insurance_policies', { id: `pol-${Date.now()}`, coverage_type: 'General Liability', carrier: '', policy_number: '', effective_date: '', expiration_date: '', limits: '', superseded: false, source_document_id: coiDoc?.id || null })}
                    data-testid="add-policy" className="text-xs font-semibold text-[#0E2A47] hover:underline">+ Add policy</button>
                </div>
              </div>
              {!coiDoc && <p className="mb-1 text-[11px] text-slate-400">Upload a COI in the provider card's Documents section to enable extraction. You can also enter policies manually now.</p>}

              {extractResult && extractResult.candidate && (
                <div className="mb-2 rounded-md border border-violet-200 bg-violet-50/60 p-2" data-testid="extract-review">
                  <div className="text-xs font-bold text-violet-800">Unconfirmed extracted candidates — review before applying</div>
                  <div className="text-[11px] text-slate-600">Method: {extractResult.method} · producer: {extractResult.candidate.producer_name || '—'} · issue date: {extractResult.candidate.certificate_issue_date || '—'}</div>
                  {(extractResult.candidate.flags || []).map((fl, i) => <div key={i} className="text-[11px] font-semibold text-amber-700" data-testid={`extract-flag-${i}`}>⚠ {fl}</div>)}
                  <ul className="mt-1 text-[11px] text-slate-700">
                    {(extractResult.candidate.policies || []).map((p, i) => <li key={i}>• {p.coverage_type}: {p.carrier || '—'} / {p.policy_number || '—'} / exp {p.expiration_date || '—'}</li>)}
                  </ul>
                  <button type="button" onClick={applyCandidate} data-testid="apply-extract-btn" className="mt-1 rounded-md bg-violet-600 px-3 py-1 text-xs font-semibold text-white hover:bg-violet-700">Apply to form (unsaved)</button>
                </div>
              )}

              {(f.insurance_policies || []).map((p, i) => {
                const st = policyStatus(p); const meta = POLICY_STATUS_META[st] || {};
                return (
                  <div key={p.id || i} className="mb-1 flex flex-wrap items-center gap-1.5" data-testid={`policy-row-${i}`}>
                    <select value={p.coverage_type || ''} onChange={(e) => setRow('insurance_policies', i, { coverage_type: e.target.value })} className="rounded border border-gray-200 px-2 py-1 text-xs">
                      {['General Liability', 'Automobile Liability', 'Workers Compensation', 'Umbrella/Excess', 'Other'].map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <Input className="h-8 w-32 text-xs" placeholder="Carrier" value={p.carrier || ''} onChange={(e) => setRow('insurance_policies', i, { carrier: e.target.value })} />
                    <Input className="h-8 w-28 text-xs" placeholder="Policy #" value={p.policy_number || ''} onChange={(e) => setRow('insurance_policies', i, { policy_number: e.target.value })} />
                    <input type="date" className="h-8 rounded border border-gray-200 px-1 text-xs" title="Effective" value={(p.effective_date || '').slice(0, 10)} onChange={(e) => setRow('insurance_policies', i, { effective_date: e.target.value })} />
                    <input type="date" className="h-8 rounded border border-gray-200 px-1 text-xs" title="Expiration" value={(p.expiration_date || '').slice(0, 10)} onChange={(e) => setRow('insurance_policies', i, { expiration_date: e.target.value })} />
                    <Input className="h-8 w-28 text-xs" placeholder="Limits" value={p.limits || ''} onChange={(e) => setRow('insurance_policies', i, { limits: e.target.value })} />
                    <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${meta.cls || ''}`} data-testid={`policy-status-${i}`}>{meta.label || st}</span>
                    <label className="flex items-center gap-1 text-[11px] text-slate-500"><input type="checkbox" checked={!!p.superseded} onChange={(e) => setRow('insurance_policies', i, { superseded: e.target.checked })} />Superseded</label>
                    <button type="button" onClick={() => delRow('insurance_policies', i)} className="text-rose-500">✕</button>
                  </div>
                );
              })}
            </div>
          </Section>

          <Section title="Compliance & Rates">
            <F label="Insurance Carrier"><Input value={f.insurance_carrier} onChange={set('insurance_carrier')} /></F>
            <F label="Policy #"><Input value={f.insurance_policy_number} onChange={set('insurance_policy_number')} /></F>
            <F label="Insurance Expiry"><Input type="date" value={(f.insurance_expiry || '').slice(0, 10)} onChange={set('insurance_expiry')} /></F>
            <F label="Hourly Rate ($)"><Input type="number" value={f.hourly_rate} onChange={set('hourly_rate')} /></F>
            <div className="flex items-center gap-6 md:col-span-2">
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" checked={!!f.coi_on_file} onChange={toggle('coi_on_file')} data-testid="prov-coi" /> COI on file
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" checked={!!f.w9_on_file} onChange={toggle('w9_on_file')} data-testid="prov-w9" /> W-9 on file
              </label>
            </div>
            <F label="Rate Notes" full><Input value={f.rate_notes} onChange={set('rate_notes')} /></F>
          </Section>

          <Section title="Notes">
            <F label="Notes" full><Textarea rows={2} value={f.notes} onChange={set('notes')} /></F>
            <F label="Internal Notes (TFS only)" full><Textarea rows={2} value={f.internal_notes} onChange={set('internal_notes')} /></F>
          </Section>
        </div>

        <div className="shrink-0 flex justify-end gap-2 border-t bg-white px-6 py-3">
          <button onClick={confirmClose} data-testid="prov-cancel" className="rounded-md border border-gray-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-gray-50">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="prov-save"
            className="rounded-md bg-[#0E2A47] px-5 py-2 text-sm font-semibold text-white hover:bg-[#16324F] disabled:opacity-50">
            {busy ? 'Saving…' : (isEdit ? 'Save Changes' : 'Create Provider')}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProviderForm;
